import "./product.scss";

import { PageProps, graphql } from "gatsby";
import {
    ProductDescription,
    ProductOptions,
    ProductPreviewGallery,
    ProductSpecsManuals,
    ProductSummary,
    RelatedProducts,
} from "../components/product";
import React, { useContext, useEffect, useRef, useState } from "react";

import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";

import { LocalType } from "../models/localType";
import ProductReviews from "../components/product/reviews/ProductReviews";
import ProductSupportService from "../components/product/supportService/ProductSupportService";

import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { ProductTemplateContext } from "../models/context/productTemplateContext";
import { IProduct } from "@sbi-international/boutik.gatsby.plugin.services";
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import SEO from "../components/Seo";
import { ProductReviewsModel } from "../models/productReviewsModel";
import MainLayout from "../components/layout/MainLayout";

export const query = graphql`
    query ProductTemplate($code: String!) {
        productsJson(code: { eq: $code }) {
            ...ProductJsonTemplate
        }
        productReviewsJson(sku: { eq: $code }) {
            sku
            average
            count
            reviews {
                nickname
                rating
                summary
                text
                created_at
                imageUrl
                sku
                review_id
            }
        }
    }
`;

export default function Product({
    data,
    pageContext: { locale, category_name },
}: PageProps<Queries.ProductTemplateQuery, ProductTemplateContext>) {
    const product = data?.productsJson as Queries.ProductJsonTemplateFragment;
    const productReviews = data?.productReviewsJson
        ? (data.productReviewsJson as ProductReviewsModel)
        : null;
    const currentLocal = locale as keyof LocalType;

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext.magento.productService;

    const { options, documents, accessories, title, code } = product;
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const [detailsBox, setDetailsBox] = useState();
    
    const path = prepareProductBreadCrumb(category_name, currentLocal);
    const [isProductReviewOpen, setProductReviewOpen] = useState(false);

    useEffect(() => {
        const country = currentLocale.includes("CA") ? "Canada" : "United States";
        const isAvailable = product.attributes?.available_country?.values?.some(
            (v) => v && v["en_CA"] === country
        );
        if (!isAvailable) {
            navigateTranslated("/");
        }
    }, [product]);

    const timeoutSetDetailsBox = (box: any) => {
        if (box !== false) {
            setDetailsBox(undefined);

            setTimeout(() => {
                setDetailsBox(box);
            }, 300);
        } else {
            setDetailsBox(box);
        }
    };

    const reviewDivRef = useRef(null);

    function showProductOptions(options: any, locale: any, isSmallView: any) {
        return (
            <ProductOptions
                className={isSmallView ? "small-view" : "large-view"}
                options={options}
                lang={locale}
            />
        );
    }

    function prepareProductBreadCrumb(category_name: any, lang: any) {
        var path: any[] = [];
        let categoryPath = category_name[lang].split("/");

        categoryPath.forEach((category: any, index: number) => {
            if (category === "") {
                return;
            }

            let url;
            switch (index) {
                case 0:
                    url = "/wood-stoves/";
                    break;
                case 1:
                    url =
                        category_name["en_CA"].split("/")[1] === "Wood stoves"
                            ? "/wood-stoves/"
                            : "/wood-inserts/";
                    break;
                default:
                    url = "/";
                    break;
            }

            var pathPart = {
                label: category,
                url: url,
            };

            path.push(pathPart);
        });

        return path;
    }

    const getMetaTitle = () => {
        if (product.attributes?.meta_title?.values?.[0]?.[currentLocale]) {
            const title = product.attributes?.meta_title?.values[0][currentLocale].split("|");
            return `${title[0]} | ${title[1]}`;
        } else {
            const category = product.category_localized?.[currentLocale]?.split("/");
            return `${title?.[currentLocale]} | ${category?.[category.length - 2]}`;
        }
    };

    const getMetaDescription = () => {
        if (product.attributes?.meta_description?.values?.[0]?.[currentLocale]) {
            return product.attributes?.meta_description?.values[0][currentLocale];
        } else {
            String(product?.description?.[currentLocal]).split("\n")?.join(",");
        }
    };

    return (
        <div className="product">
            <MainLayout>
                {" "}
                <SEO
                    title={getMetaTitle()}
                    description={getMetaDescription()}
                    schema={
                        product && {
                            product: product,
                            review: productReviews,
                        }
                    }
                />
                <div className="container-100">
                    <div className="productBreadCrumbs">
                        <Breadcrumbs path={path} separator=" > " />
                    </div>

                    <div className="container">
                        <div className="row product-main">
                            {options && showProductOptions(options, currentLocal, true)}

                            <div className="product-gallery-small-view">
                                <ProductPreviewGallery product={product} />
                                {options && showProductOptions(options, currentLocal, false)}
                            </div>

                            <ProductSummary
                                product={product as Queries.ProductsJson}
                                productReviews={productReviews}
                                setDetailsBox={timeoutSetDetailsBox}
                                reviewDivRef={reviewDivRef}
                            />
                        </div>

                        <ProductDescription product={product} />

                        {accessories && (
                            <RelatedProducts
                                accessories={
                                    accessories as Queries.ProductJsonAccessoriesFragment[]
                                }
                            />
                        )}

                        <ProductSpecsManuals
                            lang={currentLocal}
                            documents={documents as Queries.ProductJsonDocumentsFragment[]}
                            product={product}
                        />

                        <div ref={reviewDivRef}>
                            <ProductReviews
                                product={product}
                                reviews={productReviews?.reviews}
                                isProductReviewOpen={isProductReviewOpen}
                                setProductReviewOpen={setProductReviewOpen}
                            />
                        </div>
                        <ProductSupportService />
                    </div>
                </div>
            </MainLayout>
        </div>
    );
}
